<script setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import { buildQuickReplies, SEGMENTS } from '../constants/segments.js';

const props = defineProps({
  segmentId: { type: String, default: 'outros' },
  companyName: { type: String, default: '' },
});

const pix = defineModel('pix', { type: Object, required: true });
const quickReplies = defineModel('quickReplies', {
  type: Array,
  required: true,
});

const { t } = useI18n();

const pixKeyTypeOptions = [
  { id: 'cpf', name: 'CPF' },
  { id: 'cnpj', name: 'CNPJ' },
  { id: 'phone', name: 'Telefone' },
  { id: 'email', name: 'E-mail' },
  { id: 'random', name: 'Chave aleatória' },
];

const selectedPixKeyType = computed({
  get: () =>
    pixKeyTypeOptions.find(o => o.id === pix.value.pix_key_type) || null,
  set: option => {
    pix.value = { ...pix.value, pix_key_type: option?.id || '' };
  },
});

const segmentLabel = computed(() => {
  const all = [...SEGMENTS.product, ...SEGMENTS.service];
  return all.find(segment => segment.id === props.segmentId)?.label || '';
});

if (!quickReplies.value.length) {
  quickReplies.value = buildQuickReplies({
    companyName: props.companyName,
    segmentLabel: props.segmentId !== 'outros' ? segmentLabel.value : '',
    pixKey: pix.value.pix_key,
  });
}

watch(
  () => pix.value.pix_key,
  newKey => {
    const withoutPix = quickReplies.value.filter(
      reply => reply.short_code !== 'chave-pix'
    );
    quickReplies.value = newKey
      ? [
          ...withoutPix,
          {
            short_code: 'chave-pix',
            content: `Nossa chave Pix para pagamento é: ${newKey}`,
          },
        ]
      : withoutPix;
  }
);

const removeReply = index => {
  quickReplies.value = quickReplies.value.filter((_, i) => i !== index);
};

const addReply = () => {
  quickReplies.value = [...quickReplies.value, { short_code: '', content: '' }];
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col gap-3">
      <p class="text-sm font-medium text-n-slate-12">
        {{ t('QUICK_SETUP.STEP_REPLIES.PIX_TITLE') }}
      </p>
      <div class="grid grid-cols-2 gap-3">
        <SingleSelect
          v-model="selectedPixKeyType"
          :options="pixKeyTypeOptions"
          :placeholder="t('QUICK_SETUP.STEP_REPLIES.PIX_KEY_TYPE_PLACEHOLDER')"
        />
        <NextInput
          v-model="pix.pix_key"
          type="text"
          :placeholder="t('QUICK_SETUP.STEP_REPLIES.PIX_KEY_PLACEHOLDER')"
        />
        <NextInput
          v-model="pix.pix_merchant_name"
          type="text"
          :placeholder="
            t('QUICK_SETUP.STEP_REPLIES.PIX_MERCHANT_NAME_PLACEHOLDER')
          "
        />
        <NextInput
          v-model="pix.pix_merchant_city"
          type="text"
          :placeholder="
            t('QUICK_SETUP.STEP_REPLIES.PIX_MERCHANT_CITY_PLACEHOLDER')
          "
        />
      </div>
    </div>

    <div class="flex flex-col gap-3">
      <p class="text-sm font-medium text-n-slate-12">
        {{ t('QUICK_SETUP.STEP_REPLIES.REPLIES_TITLE') }}
      </p>
      <div class="flex flex-col gap-2">
        <div
          v-for="(reply, index) in quickReplies"
          :key="index"
          class="flex items-start gap-2"
        >
          <NextInput
            v-model="reply.short_code"
            type="text"
            class="w-1/3"
            :placeholder="t('QUICK_SETUP.STEP_REPLIES.SHORTCODE_PLACEHOLDER')"
          />
          <NextInput
            v-model="reply.content"
            type="text"
            class="w-full"
            :placeholder="t('QUICK_SETUP.STEP_REPLIES.CONTENT_PLACEHOLDER')"
          />
          <button
            type="button"
            class="i-lucide-trash-2 size-4 mt-3 text-n-ruby-9 shrink-0"
            @click="removeReply(index)"
          />
        </div>
      </div>
      <NextButton
        variant="faded"
        color="slate"
        size="sm"
        type="button"
        class="w-fit"
        icon="i-lucide-plus"
        :label="t('QUICK_SETUP.STEP_REPLIES.ADD_REPLY')"
        @click="addReply"
      />
    </div>
  </div>
</template>
